import auth0Client from "@/auth0";
import { UserProfileForm } from "@/features/management-settings/forms/user-profile";
import { routes } from "@/root";
import { useDialogStore, useSidebarStore } from "@/shared/stores";
import { useGetMe } from "@/spz-api/user/user";
import type { IconSymbol } from "@spz/ui/components/icon";
import {
  Sidebar,
  SidebarButton,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarUserProfile,
} from "@spz/ui/components/sidebar";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import envService from "../../env.service";

export const FULL_SIDEBAR_WIDTH = 230;
export const COLLAPSED_SIDEBAR_WIDTH = 64;

interface NavItem {
  icon: IconSymbol;
  label: string;
  to?: string;
  hidden?: boolean;
  onClick?: () => void;
  testId?: string;
}

function logout() {
  auth0Client.logout({
    logoutParams: {
      returnTo: envService.env.auth0.logoutTo,
    },
  });
}

export const AppSidebar = memo(function AppSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: me } = useGetMe();
  const { openDialog } = useDialogStore();
  const { isExpanded, toggle } = useSidebarStore();

  const mainNavItems: NavItem[] = useMemo(
    () => [
      {
        icon: "layout-dashboard",
        label: t(routes.dashboard.translationKey),
        to: routes.dashboard.path,
        hidden: true,
        testId: "sidebar-dashboard-link",
      },
      {
        icon: "list-check",
        label: t(routes.controls.translationKey),
        to: routes.controls.path,
        testId: "sidebar-controls-link",
      },
    ],
    [t],
  );

  const isPageWithoutSidebar = location.pathname.includes("/403");
  if (isPageWithoutSidebar) return null;

  return (
    <div className="shrink-0 relative">
      <Sidebar
        className="flex flex-col h-screen"
        style={{
          width: isExpanded ? FULL_SIDEBAR_WIDTH : COLLAPSED_SIDEBAR_WIDTH,
        }}
      >
        <SidebarHeader
          closeSidebarText={t("close_sidebar")}
          closeSidebarIcon={isExpanded ? "panel-left-close" : "panel-left-open"}
          onToggle={toggle}
          isExpanded={isExpanded}
        />
        <SidebarContent>
          {mainNavItems
            .filter((item) => !item.hidden)
            .map((item) => (
              <SidebarItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                active={location.pathname === item.to}
                isExpanded={isExpanded}
                data-testid={item.testId}
              >
                {item.label}
              </SidebarItem>
            ))}
        </SidebarContent>
        <SidebarFooter
          style={{
            width: isExpanded ? FULL_SIDEBAR_WIDTH : COLLAPSED_SIDEBAR_WIDTH,
          }}
          className="mt-auto relative"
        >
          <SidebarUserProfile
            name={me?.fullName ?? ""}
            className="cursor-pointer"
            isExpanded={isExpanded}
            onClick={() =>
              openDialog({
                header: { title: t("my_profile") },
                content: <UserProfileForm />,
              })
            }
            data-testid="sidebar-username"
          />
          {me?.isAdmin && (
            <SidebarButton
              key="settings_management_title"
              icon="building-2"
              className="cursor-pointer"
              onClick={() => navigate("/management-settings")}
              onKeyUp={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/management-settings");
                }
              }}
              isExpanded={isExpanded}
              data-testid="sidebar-org-settings-link"
            >
              {t("organization_settings_title")}
            </SidebarButton>
          )}
          <SidebarItem
            key="logout"
            icon="power"
            onClick={logout}
            isExpanded={isExpanded}
            data-testid="sidebar-logout-button"
          >
            {t("logout")}
          </SidebarItem>
        </SidebarFooter>
      </Sidebar>
    </div>
  );
});
